import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiCommunicationPref, ApiUser, CommunicationPrefsApi } from '@tytapp/api';
import { LoggerService, Shell } from '@tytapp/common';
import { environment } from '@tytapp/environment';
import { isOffline } from '@tytapp/environment-utils';
import { NotificationsService } from '@tytapp/notifications';
import { PushNotificationsOptinComponent } from '@tytapp/notifications/push-notifications-optin/push-notifications-optin.component';
import { UserService } from '@tytapp/user';
import { Subscription } from 'rxjs';

interface UpdateMessage {
    status: 'success'|'error',
    message: string;
}

@Component({
    selector: 'tyt-notifications-settings',
    templateUrl: './notifications-settings.component.html',
    styleUrls: ['./notifications-settings.component.scss']
})
export class NotificationsSettingsComponent implements OnInit, OnDestroy {
    private userService = inject(UserService);
    private matSnackBar = inject(MatSnackBar);
    private commPrefsApi = inject(CommunicationPrefsApi);
    private notifications = inject(NotificationsService);
    private shell = inject(Shell);
    private logger = inject(LoggerService);

    async ngOnInit() {
        this.subscriptions.add(this.userService.userChanged.subscribe(async user => {
            this.state = 'loading';
            this.user = user;

            if (isOffline()) {
                this.state = 'offline';
                return;
            }

            let res = await this.commPrefsApi.getCommunicationPrefs().toPromise();
            this.prefs = res.filter(pref => pref.category === 'notification');
            this.prefValues = this.prefs.map(({ allowed }) => allowed);
            this.state = 'ready';
        }));
    }

    state = 'loading';
    subscriptions = new Subscription();
    user: ApiUser;
    prefs: ApiCommunicationPref[];
    prefValues: boolean[];
    pushType: 'web' | 'native' = environment.isNativeBuild ? 'native' : 'web';

    get pushDevice() {
        return this.pushType === 'web' ? 'browser' : 'device';
    }

    get isPushSupported() {
        return this.notifications.webPushSupported;
    }

    get isPushEnabled() {
        return this.notifications.webPushSubscribed;
    }

    async enablePushNotifications() {
        this.shell.showDialog(PushNotificationsOptinComponent);
    }

    async disablePushNotifications() {
        await this.notifications.disablePushNotifications();
        alert(`You will no longer receive push notifications on this device.`);
    }

    notify(message: string) {
        this.matSnackBar.open(message, undefined, { duration: 3000 });
    }

    async saveChanges() {
        const patch = this.prefs.filter(({ allowed }, i) => allowed !== this.prefValues[i]);
        if (patch.length) {
            this.loading = true;
            patch.forEach(pref => pref.allowed = !pref.allowed);
            try {
                await this.commPrefsApi.patchCommunicationPrefs({
                    communication_prefs: patch
                }).toPromise();
                this.loading = false;
                this.notify('Your notification settings have been updated.');
            } catch (error) {
                this.logger.error(`Failed to update notification settings:`);
                this.logger.error(error);
                alert(
                    `Failed to save notification settings: ${error.message || error.error || 'Unknown Error'}. `
                    + `Please try again later. If the problem persists, please contact support@tytnetwork.com`
                );
                this.loading = false;
            }
        } else {
            this.notify('Your notification settings have been updated.');
        }
    }

    loading = false;

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }
}

